'use client'
// In-app "back" handling for the Android edge-swipe / back button / browser back.
//
// PROBLEM: the whole app is one page (app/page.tsx). Opening a chat, profile, story, sheet or
// another tab only changes React state - the browser history never grows. So the back gesture
// had nothing to go back to and the WebView simply closed the whole app.
//
// FIX: every "layer" (chat, profile, story, modal, non-home tab ...) pushes ONE history entry
// when it opens. Back then pops that entry and closes ONLY the top layer. At the very bottom
// (home, nothing open) a guard entry turns the first back into "Press back again to exit".
//
// Usage inside a component:   useBackLayer(isOpen, () => setIsOpen(false))
//                             useBackGuard(showHint)

import { useEffect, useRef } from 'react'

type Layer = { id: number; close: () => void }

const stack: Layer[] = []
let nextId = 1
let ignorePops = 0 // popstates caused by our own history.back() (layer closed from the UI) - not the user
let listening = false
let rootHandler: (() => void) | null = null

function pushEntry(flag: '__alLayer' | '__alGuard', id = 0) {
  try {
    // Spread the current state so Next.js' own history bookkeeping (__NA etc.) is preserved.
    window.history.pushState({ ...(window.history.state || {}), [flag]: id || 1 }, '')
  } catch { /* history unavailable - back just behaves as before */ }
}

function onPopState() {
  if (ignorePops > 0) { ignorePops--; return }
  const top = stack.pop()
  if (top) {
    top.close() // state change -> the layer's effect cleanup runs, finds it already popped, does nothing
    return
  }
  // Nothing open: we are on the root guard entry.
  rootHandler?.()
}

function ensureListener() {
  if (listening || typeof window === 'undefined') return
  window.addEventListener('popstate', onPopState)
  listening = true
}

/** While `active` is true, the back gesture calls `close` (and the app stays open). */
export function useBackLayer(active: boolean, close: () => void) {
  const closeRef = useRef(close)
  closeRef.current = close
  const gone = useRef(false)
  // Declared BEFORE the layer effect on purpose: on unmount React runs cleanups in declaration
  // order, so `gone` is already true when the layer cleanup below runs (no history.back() then).
  useEffect(() => () => { gone.current = true }, [])

  useEffect(() => {
    if (!active || typeof window === 'undefined') return
    ensureListener()
    const layer: Layer = { id: nextId++, close: () => closeRef.current() }
    stack.push(layer)
    pushEntry('__alLayer', layer.id)
    return () => {
      const i = stack.indexOf(layer)
      if (i < 0) return // closed by the back gesture itself - its entry is already gone
      const wasTop = i === stack.length - 1
      stack.splice(i, 1)
      // Closed with the X / a button: drop its history entry too, so history stays in step.
      if (wasTop && !gone.current) {
        ignorePops++
        try { window.history.back() } catch { ignorePops-- }
      }
    }
  }, [active])
}

/**
 * Bottom of the stack. Keeps one guard entry so the first back at home does not close the app:
 * it calls `onHint()` ("Press back again to exit"); a second back within 2s is let through.
 */
export function useBackGuard(enabled: boolean, onHint: () => void) {
  const hintRef = useRef(onHint)
  hintRef.current = onHint

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return
    ensureListener()
    let lastBack = 0
    // Come back to the page on top of an existing guard entry (e.g. after visiting /u/name)? Don't stack another.
    if (!(window.history.state && (window.history.state as any).__alGuard)) pushEntry('__alGuard')
    rootHandler = () => {
      const now = Date.now()
      if (now - lastBack < 2000) {
        // Second back in a row: really leave. Capacitor exits natively; in a plain browser go back a page.
        const cap = (window as any).Capacitor
        try {
          if (cap?.Plugins?.App?.exitApp) { void cap.Plugins.App.exitApp(); return }
        } catch { /* fall through */ }
        try { window.history.back() } catch { /* nothing */ }
        return
      }
      lastBack = now
      pushEntry('__alGuard') // put the guard back; we are staying
      hintRef.current()
    }
    return () => { rootHandler = null }
  }, [enabled])
}
